/**
 * The runtime's short offset name for a zone, e.g. "GMT+12", "GMT+5:45",
 * or a bare "GMT" for zones currently on UTC.
 */
function getShortOffsetName(timeZone: string): string | undefined {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'shortOffset',
  })
    .formatToParts(new Date())
    .find((part) => part.type === 'timeZoneName')?.value;
}

/**
 * Returns the current UTC offset for an IANA time zone, formatted as a short
 * label (e.g. "Pacific/Auckland" → "UTC+12", "Asia/Kathmandu" → "UTC+5:45",
 * and a bare "UTC" at zero). Returns undefined when the runtime cannot resolve
 * an offset for the zone.
 */
export function getUtcOffsetLabel(timeZone: string): string | undefined {
  const offsetMinutes = getUtcOffsetMinutes(timeZone);

  if (!Number.isFinite(offsetMinutes)) {
    return undefined;
  }

  if (offsetMinutes === 0) {
    return 'UTC';
  }

  const sign = offsetMinutes < 0 ? '-' : '+';
  const absolute = Math.abs(offsetMinutes);
  const hours = Math.floor(absolute / 60);
  const minutes = absolute % 60;

  return minutes === 0
    ? `UTC${sign}${hours}`
    : `UTC${sign}${hours}:${String(minutes).padStart(2, '0')}`;
}

/**
 * The zone's current offset from UTC in minutes, signed (e.g. "Asia/Kathmandu"
 * → 345, "America/New_York" → -300 or -240 depending on DST). Zones the runtime
 * cannot resolve return Infinity so they sort last rather than as UTC.
 */
export function getUtcOffsetMinutes(timeZone: string): number {
  const match = getShortOffsetName(timeZone)?.match(
    /^GMT(?:([+-])(\d{1,2})(?::(\d{2}))?)?$/
  );

  if (!match) {
    return Number.POSITIVE_INFINITY;
  }

  const [, sign, hours, minutes] = match;
  if (!sign) {
    return 0;
  }

  const total = Number(hours) * 60 + Number(minutes ?? 0);
  return sign === '-' ? -total : total;
}

/**
 * The browser's current IANA time zone, used as the default for company setup.
 */
export function getDefaultTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Every IANA time zone the runtime knows about. Falls back to the browser's
 * own zone on runtimes without Intl.supportedValuesOf.
 */
export function getSupportedTimeZones(): string[] {
  const defaultTimeZone = getDefaultTimeZone();

  if (typeof Intl.supportedValuesOf !== 'function') {
    return [defaultTimeZone];
  }

  const timeZones = Intl.supportedValuesOf('timeZone');
  return timeZones.includes(defaultTimeZone)
    ? timeZones
    : [defaultTimeZone, ...timeZones];
}

export interface TimezoneOption {
  value: string;
  label: string;
}

/**
 * Builds select options for every supported time zone, labelled by zone id and
 * current UTC offset (e.g. "Pacific/Auckland (UTC+12)"), ordered west to east
 * by offset and alphabetically within a shared offset.
 */
export function getTimezoneOptions(): TimezoneOption[] {
  return getSupportedTimeZones()
    .map((timeZone) => {
      const utcOffset = getUtcOffsetLabel(timeZone);

      return {
        value: timeZone,
        label: utcOffset ? `${timeZone} (${utcOffset})` : timeZone,
        offsetMinutes: getUtcOffsetMinutes(timeZone),
      };
    })
    .sort((a, b) =>
      a.offsetMinutes === b.offsetMinutes
        ? a.label.localeCompare(b.label)
        : a.offsetMinutes - b.offsetMinutes
    )
    .map(({ value, label }) => ({ value, label }));
}
